/**
 * /me/activity — Self-audit transparency page (J-X23).
 *
 * AC4: Table with Event / Time / IP columns, empty state, Load more.
 * AC5: Export CSV button — downloads current events as a CSV file.
 *
 * Fetches GET /api/me/activity?cursor=<>&limit=20 (cursor pagination per Inv 10).
 * IP is already masked server-side (xxx.xxx.xxx.***).
 *
 * Per Inv 15: all atoms from @/components/ui primitives barrel.
 */

import { type ReactElement, useState, useEffect, useCallback } from 'react';
import { Shell } from '@/components/shell';
import { Button } from '@/components/ui';
import { apiFetch } from '@/lib/api-client';

interface ActivityEvent {
  id: string;
  type: string;
  occurred_at: string | null;
  ip_masked: string | null;
}

interface ActivityResponse {
  ok: boolean;
  data: {
    events: ActivityEvent[];
    next_cursor: string | null;
  };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function eventsToCsv(events: ActivityEvent[]): string {
  const header = 'event_type,occurred_at,ip_masked\n';
  const rows = events.map((e) =>
    [
      e.type,
      e.occurred_at ?? '',
      e.ip_masked ?? '',
    ]
      .map((v) => `"${v.replace(/"/g, '""')}"`)
      .join(','),
  );
  return header + rows.join('\n');
}

export function ActivityPage(): ReactElement {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchEvents = useCallback(async (cursor: string | null, append: boolean): Promise<void> => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}&limit=20` : '?limit=20';
      const res = await apiFetch(`/api/me/activity${qs}`, { credentials: 'same-origin' });
      if (!res.ok) return;
      const data = (await res.json()) as ActivityResponse;
      if (!data.ok) return;
      if (append) {
        setEvents((prev) => [...prev, ...data.data.events]);
      } else {
        setEvents(data.data.events);
      }
      setNextCursor(data.data.next_cursor);
    } catch {
      // keep existing state on error
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    void fetchEvents(null, false);
  }, [fetchEvents]);

  const handleLoadMore = (): void => {
    if (nextCursor) void fetchEvents(nextCursor, true);
  };

  const handleExportCsv = (): void => {
    const csv = eventsToCsv(events);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bimassess-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Shell mode="individual" densityMode="editorial">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-mono text-muted-2">MY ACCOUNT</p>
          <h1 className="mt-1 font-sans text-headline font-semibold tracking-tight text-ink">My activity</h1>
        </div>
        {events.length > 0 && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleExportCsv}
          >
            Export CSV
          </Button>
        )}
      </div>

      <div className="mt-6 overflow-x-auto rounded-lg border border-line">
        {loading ? (
          <p className="px-4 py-6 text-sm text-muted">Loading…</p>
        ) : events.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted">No activity recorded yet</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-bg-warm">
                <th className="px-4 py-3 text-left font-medium text-ink-2">Event</th>
                <th className="px-4 py-3 text-left font-medium text-ink-2">Time</th>
                <th className="px-4 py-3 text-left font-medium text-ink-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} className="border-b border-line last:border-0 hover:bg-bg-warm">
                  <td className="px-4 py-3 font-mono text-xs text-ink">{event.type}</td>
                  <td className="px-4 py-3 text-muted">{formatDate(event.occurred_at)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted">{event.ip_masked ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {nextCursor && (
        <div className="mt-4 flex justify-center">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </Shell>
  );
}
